import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { UseFormReturn } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import type { TFunction } from 'i18next';

import type { HandlingUnitType, VolumeUnit, WeightUnit } from '@klareospace/pharos-lib-types';
import { getApiErrorDetail } from '@klareospace/pharos-lib-front-utils';

import { createHandlingUnitTypeSchema } from '../schemas/handling-unit-type.schema';
import type { HandlingUnitTypeFormInput } from '../schemas/handling-unit-type.schema';
import { useCreateHandlingUnitType } from './use-create-handling-unit-type';
import { useUpdateHandlingUnitType } from './use-update-handling-unit-type';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface UseHandlingUnitTypeFormReturn {
  form: UseFormReturn<HandlingUnitTypeFormInput>;
  isPending: boolean;
  onSubmit: (data: HandlingUnitTypeFormInput) => Promise<void>;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const DEFAULT_WEIGHT_UNIT: WeightUnit = 'kg';
const DEFAULT_VOLUME_UNIT: VolumeUnit = 'L';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getFormDefaults(handlingUnitType: HandlingUnitType | null): HandlingUnitTypeFormInput {
  if (handlingUnitType) {
    return {
      code: handlingUnitType.code,
      label: handlingUnitType.label,
      volumeUnit: handlingUnitType.volume.unit,
      volumeValue: String(handlingUnitType.volume.value),
      weightUnit: handlingUnitType.weight.unit,
      weightValue: String(handlingUnitType.weight.value),
    };
  }
  return {
    code: '',
    label: '',
    volumeUnit: DEFAULT_VOLUME_UNIT,
    volumeValue: '',
    weightUnit: DEFAULT_WEIGHT_UNIT,
    weightValue: '',
  };
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useHandlingUnitTypeForm(
  handlingUnitType: HandlingUnitType | null,
  onSuccess: () => void,
): UseHandlingUnitTypeFormReturn {
  const { t } = useTranslation('settings');
  const isEditMode = handlingUnitType !== null;

  const createHandlingUnitType = useCreateHandlingUnitType();
  const updateHandlingUnitType = useUpdateHandlingUnitType();

  const schema = useMemo(
    () =>
      createHandlingUnitTypeSchema(isEditMode, {
        codeFormat: t('handlingUnitTypes.drawer.validation.codeFormat'),
        codeRequired: t('handlingUnitTypes.drawer.validation.codeRequired'),
        labelRequired: t('handlingUnitTypes.drawer.validation.labelRequired'),
        positiveNumber: t('handlingUnitTypes.drawer.validation.positiveNumber'),
        volumeRequired: t('handlingUnitTypes.drawer.validation.volumeRequired'),
        weightRequired: t('handlingUnitTypes.drawer.validation.weightRequired'),
      }),
    [isEditMode, t],
  );

  const form = useForm<HandlingUnitTypeFormInput>({
    defaultValues: getFormDefaults(handlingUnitType),
    mode: 'onChange',
    resolver: zodResolver(schema),
  });

  useEffect(() => {
    form.reset(getFormDefaults(handlingUnitType));
  }, [handlingUnitType, form]);

  async function onSubmit(data: HandlingUnitTypeFormInput): Promise<void> {
    if (createHandlingUnitType.isPending || updateHandlingUnitType.isPending) return;

    const weight = { unit: data.weightUnit, value: parseFloat(data.weightValue) };
    const volume = { unit: data.volumeUnit, value: parseFloat(data.volumeValue) };

    try {
      if (isEditMode) {
        await updateHandlingUnitType.mutateAsync({
          data: { label: data.label, volume, weight },
          id: handlingUnitType.id,
        });
      } else {
        await createHandlingUnitType.mutateAsync({ code: data.code, label: data.label, volume, weight });
      }
      form.reset();
      onSuccess();
    } catch (error: unknown) {
      handleSubmitError(error, { form, isEditMode, t });
    }
  }

  return {
    form,
    isPending: createHandlingUnitType.isPending || updateHandlingUnitType.isPending,
    onSubmit,
  };
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

type SubmitErrorContext = {
  form: UseFormReturn<HandlingUnitTypeFormInput>;
  isEditMode: boolean;
  t: TFunction;
};

function handleSubmitError(error: unknown, ctx: SubmitErrorContext): void {
  const detail = getApiErrorDetail(error);

  if (detail.errorCode === 'Conflict.DuplicateHandlingUnitTypeCode') {
    ctx.form.setError('code', { message: ctx.t('handlingUnitTypes.drawer.validation.codeDuplicate') });
    return;
  }

  if (detail.errorCode === 'Conflict.DuplicateHandlingUnitTypeLabel') {
    ctx.form.setError('label', { message: ctx.t('handlingUnitTypes.drawer.validation.labelDuplicate') });
    return;
  }

  const fallbackKey = ctx.isEditMode ? 'handlingUnitTypes.toast.updateError' : 'handlingUnitTypes.toast.createError';
  toast.error(detail.message || ctx.t(fallbackKey));
}
